import { useRef } from "react";
import cn from "classnames";

/**
 * Drag handle for the right edge of a table header cell (the cell must be
 * `relative`). Reports the new width as the pointer moves; the parent clamps and
 * stores it. Double-click resets the column.
 */
export default function ColumnResizer({
  width,
  min = 60,
  onResize,
  onReset,
}: {
  width: number;
  min?: number;
  onResize: (width: number) => void;
  onReset?: () => void;
}) {
  const start = useRef<{ x: number; w: number } | null>(null);

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      title="Drag to resize"
      className={cn(
        "absolute right-0 top-0 h-full w-2 translate-x-1/2 z-10 cursor-col-resize touch-none select-none",
        "after:absolute after:left-1/2 after:top-1/4 after:h-1/2 after:w-px after:-translate-x-1/2 after:bg-base-300",
        "hover:after:bg-blue-500 hover:after:w-0.5",
      )}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        onReset?.();
      }}
      onPointerDown={(e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        start.current = { x: e.clientX, w: width };
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        onResize(Math.max(min, Math.round(start.current.w + e.clientX - start.current.x)));
      }}
      onPointerUp={(e) => {
        start.current = null;
        e.currentTarget.releasePointerCapture(e.pointerId);
      }}
    />
  );
}
